import React from "react";
import Modal from "@/components/ui/Modal";
import { formatFileSize } from "@/utils/fileUtils";
import { formatDurationHuman } from "@/utils/duration";
import {
  Video,
  Clock,
  HardDrive,
  MapPin,
  Film,
  Camera,
  CheckCircle,
  AlertCircle,
  Loader2,
  Calendar,
  CircleDot,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import type {
  VideoFile,
  VideoProcessingJob,
  VideoProcessingStatus,
} from "@/types";

interface VideoDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  video: VideoFile | null;
  processingJobs?: VideoProcessingJob[];
  processingStatus?: VideoProcessingStatus;
}

function formatDuration(seconds?: number | null): string {
  if (seconds == null) return "—";
  return formatDurationHuman(seconds);
}

function formatDateTime(dateString?: string | null): string {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString();
}

function formatElapsedTime(
  startDateString?: string | null,
  endDateString?: string | null
): string {
  if (!startDateString || !endDateString) return "—";

  const startTime = new Date(startDateString).getTime();
  const endTime = new Date(endDateString).getTime();
  if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) return "—";

  const elapsedSeconds = Math.max(
    0,
    Math.round((endTime - startTime) / 1000)
  );

  return formatDuration(elapsedSeconds);
}

function formatBitrate(bps?: number | null): string {
  if (bps == null) return "—";
  if (bps >= 1_000_000) return `${(bps / 1_000_000).toFixed(2)} Mbps`;
  if (bps >= 1_000) return `${(bps / 1_000).toFixed(1)} Kbps`;
  return `${bps} bps`;
}

function formatCoord(value?: number | null, decimals = 6): string {
  if (value == null) return "—";
  return value.toFixed(decimals);
}

function JobStatusIcon({ status }: { status: string }) {
  switch (status) {
    case "completed":
      return <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />;
    case "processing":
      return (
        <Loader2 className="w-4 h-4 text-primary-500 animate-spin flex-shrink-0" />
      );
    case "failed":
      return <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />;
    case "pending":
      return <CircleDot className="w-4 h-4 text-secondary-400 flex-shrink-0" />;
    default:
      return <Clock className="w-4 h-4 text-secondary-400 flex-shrink-0" />;
  }
}

function ProcessingStatusIcon({ status }: { status: string }) {
  switch (status) {
    case "completed":
      return <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />;
    case "processing":
      return (
        <Loader2 className="w-5 h-5 text-primary-500 animate-spin flex-shrink-0" />
      );
    case "failed":
      return <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />;
    case "pending":
      return <Clock className="w-5 h-5 text-secondary-500 flex-shrink-0" />;
    default:
      return <HardDrive className="w-5 h-5 text-secondary-400 flex-shrink-0" />;
  }
}

function JobStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation(["pages", "common"]);
  const classes: Record<string, string> = {
    completed: "bg-green-100 text-green-700",
    processing: "bg-primary-100 text-primary-700 animate-pulse",
    failed: "bg-red-100 text-red-700",
    pending: "bg-secondary-100 text-secondary-600",
    no_jobs: "bg-secondary-100 text-secondary-600",
  };
  return (
    <span
      className={`px-2 py-0.5 text-xs rounded font-medium ${classes[status] ?? "bg-secondary-100 text-secondary-600"}`}
    >
      {t(`pages:sessions.jobStatus.${status}`, { defaultValue: status })}
    </span>
  );
}

function deriveOverallStatus(processingJobs: VideoProcessingJob[]): string {
  if (processingJobs.length === 0) return "no_jobs";

  const statuses = processingJobs.map((job) => job.status);
  if (statuses.some((status) => status === "failed")) return "failed";
  if (statuses.some((status) => status === "processing")) return "processing";
  if (statuses.every((status) => status === "completed")) return "completed";
  return "pending";
}

function getProcessingSummary(
  status: string,
  completedJobCount: number,
  failedJobCount: number,
  jobCount: number,
  t: TFunction
) {
  switch (status) {
    case "completed":
      return {
        title: t("pages:sessions.videoDetails.processing.allReadyTitle"),
        description: t("pages:sessions.videoDetails.processing.allReadyDesc", { completed: completedJobCount, total: jobCount }),
      };
    case "processing":
      return {
        title: t("pages:sessions.videoDetails.processing.encodingTitle"),
        description: t("pages:sessions.videoDetails.processing.encodingDesc", { completed: completedJobCount, total: jobCount }),
      };
    case "failed":
      return {
        title: t("pages:sessions.videoDetails.processing.needsAttentionTitle"),
        description: t("pages:sessions.videoDetails.processing.needsAttentionDesc", { failed: failedJobCount, total: jobCount }),
      };
    case "pending":
      return {
        title: t("pages:sessions.videoDetails.processing.waitingTitle"),
        description: t("pages:sessions.videoDetails.processing.waitingDesc", { total: jobCount }),
      };
    default:
      return {
        title: t("pages:sessions.videoDetails.processing.noJobsTitle"),
        description: t("pages:sessions.videoDetails.processing.noJobsDesc"),
      };
  }
}

function getDisplayMessage(message?: string | null): string | null {
  if (!message) return null;

  const trimmedMessage = message.trim();
  if (!trimmedMessage) return null;

  if (
    (trimmedMessage.startsWith("{") && trimmedMessage.endsWith("}")) ||
    (trimmedMessage.startsWith("[") && trimmedMessage.endsWith("]"))
  ) {
    try {
      JSON.parse(trimmedMessage);
      return null;
    } catch {
      return message;
    }
  }

  return message;
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start gap-4 py-2 border-b border-secondary-100 last:border-0">
      <span className="text-sm text-secondary-500 flex-shrink-0">{label}</span>
      <span className="text-sm text-secondary-900 text-right break-all">
        {value}
      </span>
    </div>
  );
}

export default function VideoDetailsModal({
  isOpen,
  onClose,
  video,
  processingJobs = [],
  processingStatus,
}: VideoDetailsModalProps) {
  const { t } = useTranslation(["pages", "common"]);
  if (!video) return null;

  const jobs = processingStatus?.processingJobs ?? processingJobs;
  const jobCount = processingStatus?.jobCount ?? jobs.length;
  const completedJobCount =
    processingStatus?.completedJobCount ??
    jobs.filter((job) => job.status === "completed").length;
  const failedJobCount =
    processingStatus?.failedJobCount ??
    jobs.filter((job) => job.status === "failed").length;
  const overallStatus =
    processingStatus?.overallStatus ?? deriveOverallStatus(jobs);
  const processingSummary = getProcessingSummary(
    overallStatus,
    completedJobCount,
    failedJobCount,
    jobCount,
    t
  );

  const hasGps =
    video.gpsLatitude != null ||
    video.gpsLongitude != null ||
    video.gpsAltitude != null;
  const hasCameraInfo = video.cameraModel || video.cameraManufacturer;
  const hasTechInfo =
    video.videoCodec ||
    video.audioCodec ||
    video.videoBitrate != null ||
    video.videoResolution ||
    video.frameRate != null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("pages:sessions.videoDetails.title")} size="lg">
      <div className="space-y-6">
        {/* File info */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Video className="w-4 h-4 text-secondary-400" />
            <h3 className="text-sm font-semibold text-secondary-700 uppercase tracking-wide">
              {t("pages:sessions.videoDetails.fileInfo")}
            </h3>
          </div>
          <div className="bg-secondary-50 rounded-lg px-4">
            <InfoRow label={t("pages:sessions.videoDetails.fields.filename")} value={video.filename} />
            <InfoRow
              label={t("pages:sessions.videoDetails.fields.fileSize")}
              value={formatFileSize(video.fileSize, { unknownLabel: "—" })}
            />
            <InfoRow label={t("pages:sessions.videoDetails.fields.contentType")} value={video.contentType ?? "—"} />
            <InfoRow
              label={t("pages:sessions.videoDetails.fields.uploadStatus")}
              value={
                <span
                  className={`${
                    video.uploadStatus === "completed"
                      ? "text-green-600"
                      : video.uploadStatus === "failed"
                        ? "text-red-600"
                        : "text-primary-600"
                  }`}
                >
                  {t(`pages:sessions.videoStatus.${video.uploadStatus}`, { defaultValue: video.uploadStatus })}
                </span>
              }
            />
            <InfoRow
              label={t("pages:sessions.videoDetails.fields.uploadedAt")}
              value={formatDateTime(video.uploadedAt)}
            />
          </div>
        </div>

        {/* Recording info */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-secondary-400" />
            <h3 className="text-sm font-semibold text-secondary-700 uppercase tracking-wide">
              {t("pages:sessions.videoDetails.recording")}
            </h3>
          </div>
          <div className="bg-secondary-50 rounded-lg px-4">
            <InfoRow
              label={t("pages:sessions.videoDetails.fields.recordedAt")}
              value={formatDateTime(video.recordedAt)}
            />
            <InfoRow label={t("pages:sessions.videoDetails.fields.duration")} value={formatDuration(video.duration)} />
          </div>
        </div>

        {/* Technical specs */}
        {hasTechInfo && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Film className="w-4 h-4 text-secondary-400" />
            <h3 className="text-sm font-semibold text-secondary-700 uppercase tracking-wide">
              {t("pages:sessions.videoDetails.techSpecs")}
            </h3>
          </div>
          <div className="bg-secondary-50 rounded-lg px-4">
            {video.videoResolution && (
              <InfoRow label={t("pages:sessions.videoDetails.fields.resolution")} value={video.videoResolution} />
            )}
            {video.videoCodec && (
              <InfoRow label={t("pages:sessions.videoDetails.fields.videoCodec")} value={video.videoCodec} />
            )}
            {video.audioCodec && (
              <InfoRow label={t("pages:sessions.videoDetails.fields.audioCodec")} value={video.audioCodec} />
            )}
            {video.videoBitrate != null && (
              <InfoRow
                label={t("pages:sessions.videoDetails.fields.bitrate")}
                value={formatBitrate(video.videoBitrate)}
              />
            )}
            {video.frameRate != null && (
              <InfoRow label={t("pages:sessions.videoDetails.fields.frameRate")} value={`${video.frameRate} fps`} />
            )}
            </div>
          </div>
        )}

        {/* Camera info */}
        {hasCameraInfo && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Camera className="w-4 h-4 text-secondary-400" />
            <h3 className="text-sm font-semibold text-secondary-700 uppercase tracking-wide">
              {t("pages:sessions.videoDetails.camera")}
            </h3>
          </div>
          <div className="bg-secondary-50 rounded-lg px-4">
            {video.cameraManufacturer && (
              <InfoRow
                label={t("pages:sessions.videoDetails.fields.manufacturer")}
                value={video.cameraManufacturer}
              />
            )}
            {video.cameraModel && (
              <InfoRow label={t("pages:sessions.videoDetails.fields.model")} value={video.cameraModel} />
            )}
            </div>
          </div>
        )}

        {/* GPS / Geolocation */}
        {hasGps && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4 text-secondary-400" />
            <h3 className="text-sm font-semibold text-secondary-700 uppercase tracking-wide">
              {t("pages:sessions.videoDetails.geolocation")}
            </h3>
          </div>
          <div className="bg-secondary-50 rounded-lg px-4">
            <InfoRow
              label={t("pages:sessions.videoDetails.fields.latitude")}
              value={`${formatCoord(video.gpsLatitude)}°`}
            />
            <InfoRow
              label={t("pages:sessions.videoDetails.fields.longitude")}
              value={`${formatCoord(video.gpsLongitude)}°`}
            />
            {video.gpsAltitude != null && (
              <InfoRow
                label={t("pages:sessions.videoDetails.fields.altitude")}
                value={`${video.gpsAltitude.toFixed(1)} m`}
              />
            )}
            </div>
          </div>
        )}

        {/* Processing jobs */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <HardDrive className="w-4 h-4 text-secondary-400" />
            <h3 className="text-sm font-semibold text-secondary-700 uppercase tracking-wide">
              {t("pages:sessions.videoDetails.processingJobs")}
            </h3>
          </div>
          {jobCount > 0 && (
            <div className="bg-secondary-50 rounded-lg px-4 py-3 mb-3">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <ProcessingStatusIcon status={overallStatus} />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-secondary-900">
                        {processingSummary.title}
                      </p>
                      <JobStatusBadge status={overallStatus} />
                    </div>
                    <p className="text-sm text-secondary-500 mt-1">
                      {processingSummary.description}
                    </p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-secondary-700 whitespace-nowrap">
                  {completedJobCount}/{jobCount} {t("pages:sessions.videoDetails.ready")}
                </span>
              </div>
            </div>
          )}

          {jobs.length === 0 ? (
            <p className="text-sm text-secondary-500 bg-secondary-50 rounded-lg px-4 py-3">
              {t("pages:sessions.videoDetails.noJobs")}
            </p>
          ) : (
            <div className="divide-y divide-secondary-100 rounded-lg bg-secondary-50 overflow-hidden">
              {jobs.map((job) => {
                const displayMessage = getDisplayMessage(job.message);

                return (
                  <div key={job.id} className="px-4 py-3">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div className="flex items-start gap-2 min-w-0">
                        <JobStatusIcon status={job.status} />
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-secondary-900">
                            {job.resolutionLabel}
                          </span>
                          {displayMessage && (
                            <p className="text-xs text-secondary-500 mt-0.5 break-words">
                              {displayMessage}
                            </p>
                          )}
                          {job.error && (
                            <p className="text-xs text-red-600 mt-0.5 break-words">
                              {job.error}
                            </p>
                          )}
                        </div>
                      </div>
                      <JobStatusBadge status={job.status} />
                    </div>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs text-secondary-500 sm:ml-6">
                      {job.createdAt && (
                        <div>
                          <span className="block font-medium text-secondary-600">
                            {t("pages:sessions.videoDetails.queued")}
                          </span>
                          <span>{formatDateTime(job.createdAt)}</span>
                        </div>
                      )}
                      {job.startedAt && (
                        <div>
                          <span className="block font-medium text-secondary-600">
                            {t("pages:sessions.videoDetails.started")}
                          </span>
                          <span>{formatDateTime(job.startedAt)}</span>
                        </div>
                      )}
                      {job.completedAt && (
                        <div>
                          <span className="block font-medium text-secondary-600">
                            {t("pages:sessions.videoDetails.completed")}
                          </span>
                          <span>{formatDateTime(job.completedAt)}</span>
                        </div>
                      )}
                      {job.startedAt && job.completedAt && (
                        <div>
                          <span className="block font-medium text-secondary-600">
                            {t("pages:sessions.videoDetails.processingTime")}
                          </span>
                          <span>
                            {formatElapsedTime(job.startedAt, job.completedAt)}
                          </span>
                        </div>
                      )}
                      {job.updatedAt && !job.completedAt && (
                        <div>
                          <span className="block font-medium text-secondary-600">
                            {t("pages:sessions.videoDetails.lastUpdate")}
                          </span>
                          <span>{formatDateTime(job.updatedAt)}</span>
                        </div>
                      )}
                      {job.hasOutput && job.outputFileSize != null && (
                        <div>
                          <span className="block font-medium text-secondary-600">
                            {t("pages:sessions.videoDetails.outputSize")}
                          </span>
                          <span>
                            {formatFileSize(job.outputFileSize, {
                              unknownLabel: "—",
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
